import { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';

import ChevronDown from '@/icons/light/chevron-down.svg';

import { formatNumberToCurrency } from '@/utils';

import type { CustomSelectProps } from './types';

export default function CustomSelect({
  name,
  options,
  setterFunction,
  placeholder,
  selected,
  formatOption,
  className = '',
}: CustomSelectProps) {
  // Histórico: sin formatter los valores numéricos se muestran como moneda
  // (los selects de precio). `formatOption` permite reutilizar el select
  // para otras unidades (p.ej. "80 m²").
  const format = (val: number) =>
    formatOption
      ? formatOption(val)
      : !isNaN(val)
        ? formatNumberToCurrency(val, 2, 2)
        : val;
  return (
    <div className={`text-body text-body-s relative ${className}`}>
      <Listbox value={selected} onChange={setterFunction}>
        {({ open }) => (
          <>
            {/* `w-48` es el ancho por defecto; `w-full` deja que mande el
                contenedor cuando éste recibe un ancho (p.ej. el `flex-1` de la
                barra de filtros del listado). */}
            <Listbox.Button className="text-placeholder border-neutrals-500 bg-neutrals-0 relative w-48 max-w-full rounded-full border py-3 pr-10 pl-4 text-left lg:w-full">
              <span className="block truncate">
                {selected === 0 ? placeholder : format(selected)}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDown
                  className={`h-6 w-6 transition ${open && 'rotate-180'}`}
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              {/* `anchor` portaliza el dropdown (HeadlessUI v2): así escapa del
                  `overflow-x-auto` de la barra de filtros mobile, que si no lo
                  recortaba y dejaba las opciones inalcanzables. */}
              <Listbox.Options
                anchor={{ to: 'bottom start', gap: 4 }}
                // El anchor portaliza el panel fuera del wrapper, así que la
                // tipografía no se hereda y hay que declararla aquí.
                className="text-body text-body-s rounded-custom border-neutrals-100 bg-bg-neutrals-0 shadow-light-s z-20 max-h-80! w-48 overflow-y-scroll border"
              >
                <Listbox.Option
                  value={0}
                  className="justify-left border-neutrals-50 text-text-neutrals-500 hover:bg-neutrals-50 flex cursor-pointer gap-4 border-b py-3 pr-2 pl-5 last:border-0"
                >
                  Sin definir
                </Listbox.Option>
                {options.map((val, idx) => (
                  <Listbox.Option
                    key={`${idx}-${name}`}
                    className="justify-left border-neutrals-50 text-text-neutrals-500 hover:bg-neutrals-50 flex cursor-pointer gap-4 border-b py-3 pr-2 pl-5 last:border-0"
                    value={val}
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected
                              ? 'text-text-neutrals-900 font-bold'
                              : 'font-normal'
                          }`}
                        >
                          {format(val)}
                        </span>
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  );
}
